import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { ModalMoreTimeDataModel, SelectedTimesModel } from 'src/models/book-job.model';

import { WindowService } from "src/shared/providers/window.service";

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

@Component({
    templateUrl: 'modal-more-times.html',
    encapsulation: ViewEncapsulation.None,
    selector: 'book-job',
    standalone: false
})
export class ModalMoreTimesPage implements OnInit, OnDestroy {

  @Input() firstTime: any;
  @Input() thriceAWeek: boolean;
  @Input() dateTimes: any[];
  @Input() frequencyLabel: string;
  @Input() selectedPrice;
  @Input() eventEmitter: EventEmitter<ModalMoreTimeDataModel>;

  form: UntypedFormGroup;
  closeSecondTime = false;
  completed = false;

  second_time;
  third_time;

  secondDateLabelText = '';
  thirdDateLabelText = '';
  isFormValidState = false;

  topNavHeight = 0;
  private resizeObserver: ResizeObserver;
  private destroyRef = inject(DestroyRef);


  boundConfirmSelections = this.confirmSelections.bind(this);

constructor(
    private fb: UntypedFormBuilder,
    private modalCtrl: ModalController,
    private windowService: WindowService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      second_time: ['', Validators.required],
      third_time: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.observeTopNavHeight();
    this.updateDerivedState();
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateDerivedState();
    });
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  private observeTopNavHeight() {
    const element = document.getElementById('top-nav-height');
      if (element) {
        this.topNavHeight = element.offsetHeight + 20;
        this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          this.topNavHeight = entry.contentRect.height + 20;
          this.changeDetectorRef.detectChanges();
        }
      });
      this.resizeObserver.observe(element);
    }
  }

  closeModal() {
    this.modalCtrl.dismiss({
      completed: this.completed,
      timesSelected: this.getTimesSelected()
    });
  }

  secondDateLabel() {
    const selected = this.selectedTimeFor(this.form.value.second_time, this.second_time);
    if (this.form.value.second_time === '' || !selected?.value) {
      return 'Select Second Time';
    }
    const date = this.formatDate(selected.value, 'dddd MMM d');
    return [date, selected.viewValue].filter(Boolean).join(' at ');
  }

  thirdDateLabel() {
    const selected = this.selectedTimeFor(this.form.value.third_time, this.third_time);
    if (this.form.value.third_time === '' || !selected?.value) {
      return 'Select Third Time';
    }
    const date = this.formatDate(selected.value, 'dddd MMM d');
    return [date, selected.viewValue].filter(Boolean).join(' at ');
  }

  // The radio's onChange writes the form value before emitting (optionChange), so a
  // valueChanges-driven label refresh can run before second_time/third_time is set.
  // Resolve the selected item from the form value (single source of truth), falling
  // back to the class field once the list has been filtered down.
  private selectedTimeFor(formValue: any, fallback: any) {
    return this.dateTimes?.find(dateTime => dateTime?.value === formValue) ?? fallback;
  }

  selectSecondTime(dateTime, idx) {
    this.second_time = dateTime;
    if (this.thriceAWeek) {
      this.closeSecondTime = true;
      this.dateTimes = this.dateTimes.filter((_, i) => i > idx);
    } else {
      this.completed = true;
      const data = {completed: true, timesSelected: this.getTimesSelected()};
      this.eventEmitter.emit(data);
      if (this.windowService.isDesktopRes) {
        this.closeModal();
      }
    }
  }

  selectThirdTime(dateTime) {
    this.third_time = dateTime;
    this.completed = true;
    const data = {completed: true, timesSelected: this.getTimesSelected()};
    this.eventEmitter.emit(data);
    if (this.windowService.isDesktopRes) {
      this.closeModal();
    }
  }

  isFormValid() {
    const secondTimeFilled = this.form.value.second_time !== '';
    const thirdTimeFilled = this.form.value.third_time !== '';

    return secondTimeFilled && (!this.thriceAWeek || thirdTimeFilled);
  }

  private updateDerivedState() {
    this.secondDateLabelText = this.secondDateLabel();
    this.thirdDateLabelText = this.thirdDateLabel();
    this.isFormValidState = this.isFormValid();
  }

  confirmSelections() {
    const timesSelected = this.getTimesSelected();
    this.modalCtrl.dismiss({ completed: true, timesSelected });
  }

  getTimesSelected(): Array<SelectedTimesModel> {
    return this.thriceAWeek
    ? [this.second_time, this.third_time]
    : [this.second_time];
  }

  formatDate(dateTime, format) {
    return new ParseDateTimePipe().transform(dateTime, format)
  }

}
